import React, { useState, useEffect, useCallback, useRef } from "react";
import { App } from "antd";
import { useAuth } from "../../hooks/useAuth";
import PermissionWrapper, {
  AdminOnly,
  PermissionButton,
} from "../common/PermissionWrapper";
import { PERMISSIONS } from "../../utils/permissions";
import apiService from "../../services/api";
import { formatDateTime } from "../../utils/formatters";
import "./ExchangeManagement.css";
import "../common/PermissionWrapper.css";
import {
  Plus,
  Search,
  Building2,
  Edit2,
  Trash2,
  Power,
  X,
  ChevronLeft,
  ChevronRight,
  Globe,
  Link as LinkIcon,
  Hash,
  FileText,
  SortAsc,
  CheckCircle,
  XCircle,
} from "lucide-react";

const ExchangeManagement = () => {
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentExchange, setCurrentExchange] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const lastParamsRef = useRef(null);

  const { modal } = App.useApp();

  const [formData, setFormData] = useState({
    code: "",
    name: "",
    description: "",
    logo_url: "",
    website: "",
    is_active: true,
    sort_order: 0,
  });

  const loadExchanges = useCallback(async (opts = {}) => {
    const force = opts.force === true;
    setLoading(true);
    setError("");
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
        ...(debouncedSearchTerm && { code: debouncedSearchTerm }),
        ...(statusFilter && { is_active: statusFilter === "active" }),
      };

      const paramsString = JSON.stringify(params);
      if (!force && lastParamsRef.current === paramsString) {
        setLoading(false);
        return;
      }
      lastParamsRef.current = paramsString;

      const response = await apiService.exchanges.getExchanges(params);
      if (!response.data.success) {
        throw new Error(response.data.message || "获取交易所列表失败");
      }
      const exchangesList = response.data.data?.list || [];
      const totalCount = response.data.data?.total || 0;
      setExchanges(exchangesList);
      setTotal(totalCount);
    } catch (err) {
      setError(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "加载交易所列表失败",
      );
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearchTerm, statusFilter, pageSize]);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to first page when search changes
      if (searchTerm !== debouncedSearchTerm) {
        setCurrentPage(1);
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, debouncedSearchTerm]);

  useEffect(() => {
    loadExchanges();
  }, [loadExchanges]);

  const handleCreateExchange = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      await apiService.exchanges.createExchange({
        ...formData,
        code: formData.code.toLowerCase().trim(),
        sort_order: coerceSortOrder(formData.sort_order),
      });
      setSuccess("交易所创建成功");
      setShowCreateModal(false);
      resetForm();
      loadExchanges({ force: true });
    } catch (err) {
      setError(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "创建交易所失败",
      );
    }
  };

  const handleUpdateExchange = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      await apiService.exchanges.updateExchange(currentExchange.id, {
        name: formData.name,
        description: formData.description,
        logo_url: formData.logo_url,
        website: formData.website,
        is_active: formData.is_active,
        sort_order: coerceSortOrder(formData.sort_order),
      });
      setSuccess("交易所更新成功");
      setShowEditModal(false);
      resetForm();
      loadExchanges({ force: true });
    } catch (err) {
      setError(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "更新交易所失败",
      );
    }
  };

  const handleDeleteExchange = async (exchangeId, exchangeName) => {
    modal.confirm({
      title: "删除交易所",
      content: `确定要删除交易所 "${exchangeName}" 吗？此操作不可撤销。`,
      okText: "确定",
      cancelText: "取消",
      okType: "danger",
      onOk: async () => {
        setError("");
        setSuccess("");

        try {
          await apiService.exchanges.deleteExchange(exchangeId);
          setSuccess("交易所删除成功");
          loadExchanges({ force: true });
        } catch (err) {
          setError(
            err.response?.data?.error?.message ||
            err.response?.data?.message ||
            err.message ||
            "删除交易所失败",
          );
        }
      },
    });
  };

  const handleToggleStatus = async (exchangeId, currentStatus) => {
    const newStatus = !currentStatus;
    const action = newStatus ? "启用" : "禁用";

    modal.confirm({
      title: `${action}交易所`,
      content: `确定要${action}该交易所吗？`,
      okText: "确定",
      cancelText: "取消",
      okType: newStatus ? "primary" : "danger",
      onOk: async () => {
        setError("");
        setSuccess("");

        try {
          await apiService.exchanges.updateExchangeStatus(exchangeId, newStatus);
          setSuccess(`交易所${action}成功`);
          loadExchanges({ force: true });
        } catch (err) {
          setError(
            err.response?.data?.error?.message ||
            err.response?.data?.message ||
            err.message ||
            `${action}交易所失败`,
          );
        }
      },
    });
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (exchange) => {
    setCurrentExchange(exchange);
    setFormData({
      code: exchange.code,
      name: exchange.name,
      description: exchange.description || "",
      logo_url: exchange.logo_url || "",
      website: exchange.website || "",
      is_active: exchange.is_active,
      sort_order: exchange.sort_order || 0,
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      description: "",
      logo_url: "",
      website: "",
      is_active: true,
      sort_order: 0,
    });
    setCurrentExchange(null);
  };

  /** 数字输入框在 React 中为 string，API 需要 JSON number */
  const coerceSortOrder = (v) => {
    const n = Number.parseInt(String(v ?? ""), 10);
    return Number.isFinite(n) ? n : 0;
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };


  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="exchange-management animate-fade-in">
      <div className="page-header">
        <div className="header-title">
          <Building2 className="header-icon" />
          <h1>交易所管理</h1>
        </div>
        <AdminOnly>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus className="btn-icon" />
            添加交易所
          </button>
        </AdminOnly>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* 搜索和筛选 */}
      <div className="filters">
        <div className="search-box">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="搜索交易所代码..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">所有状态</option>
          <option value="active">启用</option>
          <option value="inactive">禁用</option>
        </select>
      </div>

      {/* 交易所列表 */}
      <div className="table-container">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <span>加载中...</span>
          </div>
        ) : (
          <table className="exchanges-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>
                  <Hash className="th-icon" />
                  代码
                </th>
                <th>
                  <Building2 className="th-icon" />
                  名称
                </th>
                <th>
                  <FileText className="th-icon" />
                  描述
                </th>
                <th>
                  <Globe className="th-icon" />
                  官网
                </th>
                <th>
                  <Power className="th-icon" />
                  状态
                </th>
                <th>
                  <SortAsc className="th-icon" />
                  排序
                </th>
                <th>
                  更新时间
                </th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {exchanges.map((exchange) => (
                <tr key={exchange.id}>
                  <td>{exchange.id}</td>
                  <td>
                    <code className="exchange-code">{exchange.code}</code>
                  </td>
                  <td className="exchange-name">{exchange.name}</td>
                  <td className="exchange-description">
                    {exchange.description || "-"}
                  </td>
                  <td>
                    {exchange.website ? (
                      <a
                        href={exchange.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="website-link"
                      >
                        <LinkIcon className="link-icon" />
                        访问
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${exchange.is_active ? "active" : "inactive"}`}>
                      {exchange.is_active ? (
                        <>
                          <CheckCircle className="badge-icon" />
                          启用
                        </>
                      ) : (
                        <>
                          <XCircle className="badge-icon" />
                          禁用
                        </>
                      )}
                    </span>
                  </td>
                  <td>{exchange.sort_order}</td>
                  <td>{formatDateTime(exchange.updated_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <AdminOnly>
                        <button
                          className="btn btn-sm btn-secondary btn-icon-only"
                          onClick={() => openEditModal(exchange)}
                          title="编辑"
                        >
                          <Edit2 className="btn-icon" />
                        </button>
                        <button
                          className={`btn btn-sm ${exchange.is_active ? "btn-warning" : "btn-success"} btn-icon-only`}
                          onClick={() => handleToggleStatus(exchange.id, exchange.is_active)}
                          title={exchange.is_active ? "禁用" : "启用"}
                        >
                          <Power className="btn-icon" />
                        </button>
                        <button
                          className="btn btn-sm btn-danger btn-icon-only"
                          onClick={() => handleDeleteExchange(exchange.id, exchange.name)}
                          title="删除"
                        >
                          <Trash2 className="btn-icon" />
                        </button>
                      </AdminOnly>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-sm"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            <ChevronLeft className="btn-icon" />
            上一页
          </button>
          <span className="page-info">
            第 {currentPage} 页，共 {totalPages} 页，总计 {total} 条记录
          </span>
          <button
            className="btn btn-sm"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            下一页
            <ChevronRight className="btn-icon" />
          </button>
        </div>
      )}

      {/* 创建交易所模态框 */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <Building2 className="modal-icon" />
                <h2>添加交易所</h2>
              </div>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <X />
              </button>
            </div>
            <form onSubmit={handleCreateExchange} className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>代码 *</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleFormChange}
                    placeholder="如: binance"
                    required
                    className="form-input"
                    maxLength={50}
                  />
                  <small className="form-help">唯一标识符，只能包含字母、数字、连字符</small>
                </div>
                <div className="form-group">
                  <label>名称 *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    placeholder="如: Binance"
                    required
                    className="form-input"
                    maxLength={100}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>描述</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="交易所描述..."
                  className="form-textarea"
                  rows={2}
                  maxLength={500}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Logo URL</label>
                  <input
                    type="url"
                    name="logo_url"
                    value={formData.logo_url}
                    onChange={handleFormChange}
                    placeholder="https://..."
                    className="form-input"
                    maxLength={500}
                  />
                </div>
                <div className="form-group">
                  <label>官网地址</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleFormChange}
                    placeholder="https://..."
                    className="form-input"
                    maxLength={200}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>排序顺序</label>
                  <input
                    type="number"
                    name="sort_order"
                    value={formData.sort_order}
                    onChange={handleFormChange}
                    className="form-input"
                    min={0}
                  />
                  <small className="form-help">数字越小排序越靠前</small>
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleFormChange}
                    />
                    <span className="checkbox-text">启用</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  创建
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 编辑交易所模态框 */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <Edit2 className="modal-icon" />
                <h2>编辑交易所</h2>
              </div>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>
                <X />
              </button>
            </div>
            <form onSubmit={handleUpdateExchange} className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>代码</label>
                  <input
                    type="text"
                    value={currentExchange?.code || ""}
                    className="form-input"
                    disabled
                  />
                  <small className="form-help">代码不可修改</small>
                </div>
                <div className="form-group">
                  <label>名称 *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    className="form-input"
                    maxLength={100}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>描述</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  className="form-textarea"
                  rows={2}
                  maxLength={500}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Logo URL</label>
                  <input
                    type="url"
                    name="logo_url"
                    value={formData.logo_url}
                    onChange={handleFormChange}
                    className="form-input"
                    maxLength={500}
                  />
                </div>
                <div className="form-group">
                  <label>官网地址</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleFormChange}
                    className="form-input"
                    maxLength={200}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>排序顺序</label>
                  <input
                    type="number"
                    name="sort_order"
                    value={formData.sort_order}
                    onChange={handleFormChange}
                    className="form-input"
                    min={0}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleFormChange}
                    />
                    <span className="checkbox-text">启用</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  保存
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExchangeManagement;
